/* Keep fragrance and discovery-set browsing in-page; ordinary links remain the no-JavaScript fallback. */
(() => {
  if (window.nuerCollectionAudience) return;
  window.nuerCollectionAudience = true;
  const selector = '[data-collection-audience]';
  const initialNav = document.querySelector(selector);
  if (!initialNav) return;
  const basePath = new URL(initialNav.dataset.collectionUrl, location.origin).pathname;
  const fragrancePath = new URL(initialNav.dataset.fragranceUrl || '/collections/all', location.origin).pathname;
  const discoveryPath = new URL(initialNav.dataset.discoveryUrl || '/collections/discovery-sets', location.origin).pathname;
  const cache = new Map();
  let navigation = 0;
  let controller;
  const isCollection = (url) => url.origin === location.origin && (
    url.pathname === basePath || url.pathname.startsWith(`${basePath}/`) ||
    url.pathname === fragrancePath || url.pathname.startsWith(`${fragrancePath}/`) ||
    url.pathname === discoveryPath
  );
  const cleanURL = (url) => {
    const result = new URL(url);
    result.hash = '';
    return result;
  };
  const cancel = () => {
    navigation++;
    controller?.abort();
    document.querySelector('main')?.removeAttribute('aria-busy');
    document.querySelector(selector)?.removeAttribute('data-loading');
  };
  const load = async (url, signal) => {
    const key = url.href;
    if (cache.has(key)) return cache.get(key);
    const response = await fetch(key, { signal, headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!response.ok) throw new Error('Unable to load collection');
    const html = await response.text();
    const page = new DOMParser().parseFromString(html, 'text/html');
    if (!page.querySelector(`${selector}`) || !page.querySelector('#ProductGridContainer')) {
      throw new Error('Missing collection content');
    }
    cache.set(key, html);
    if (cache.size > 16) cache.delete(cache.keys().next().value);
    return html;
  };
  const destination = (link) => {
    const url = cleanURL(link.href);
    url.search = location.search;
    if (link.hasAttribute('data-sort')) {
      if (link.dataset.sort) url.searchParams.set('sort_by', link.dataset.sort);
      else url.searchParams.delete('sort_by');
    }
    if (link.hasAttribute('data-reset-filters')) {
      for (const key of [...url.searchParams.keys()]) {
        if (key.startsWith('filter.')) url.searchParams.delete(key);
      }
    }
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    return url;
  };
  const prefetch = () => {
    const connection = navigator.connection;
    if (connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || '')) return;
    document.querySelectorAll(`${selector} a`).forEach((link) => {
      const url = destination(link);
      if (isCollection(url)) load(url).catch(() => {});
    });
  };
  const navigate = async (url, push, label) => {
    cancel();
    const request = navigation;
    controller = new AbortController();
    const main = document.querySelector('main');
    main.setAttribute('aria-busy', 'true');
    document.querySelector(selector)?.setAttribute('data-loading', 'true');
    try {
      const html = await load(url, controller.signal);
      if (request !== navigation) return;
      const page = new DOMParser().parseFromString(html, 'text/html');
      const incoming = page.querySelector('main');
      if (!incoming) throw new Error('Missing main content');
      if (push) history.pushState({ searchParams: url.search.slice(1) }, '', url);
      main.replaceChildren(...incoming.childNodes);
      document.title = page.title;
      // Keep Dawn's native facet/history state aligned with the newly rendered path.
      if (typeof FacetFiltersForm !== 'undefined') {
        FacetFiltersForm.searchParamsPrev = url.search.slice(1);
      }
      if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger();
      if (label) {
        const selected = [...main.querySelectorAll(`${selector} a`)].find((link) => link.textContent.trim() === label);
        selected?.focus({ preventScroll: true });
      }
      prefetch();
    } catch (error) {
      if (error.name !== 'AbortError' && request === navigation) location.assign(url.href);
    } finally {
      if (request === navigation) {
        main.removeAttribute('aria-busy');
        document.querySelector(selector)?.removeAttribute('data-loading');
      }
    }
  };
  document.addEventListener('click', (event) => {
    const link = event.target.closest(`${selector} a`);
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const url = destination(link);
    if (!isCollection(url)) return;
    event.preventDefault();
    if (url.href === cleanURL(location.href).href) {
      cancel();
      return;
    }
    navigate(url, true, link.textContent.trim());
  });
  // Capture first so Dawn's query-only handler cannot render a stale audience on Back/Forward.
  window.addEventListener('popstate', (event) => {
    const url = cleanURL(location.href);
    if (!isCollection(url)) return;
    event.stopImmediatePropagation();
    navigate(url, false);
  }, true);
  document.addEventListener('nuer:facets-update', cancel);
  if ('requestIdleCallback' in window) requestIdleCallback(prefetch, { timeout: 1500 });
  else setTimeout(prefetch, 200);
})();
